import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useCreatorMe } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import styles from './Hub.module.css';

const DOS = [
  'Disclose the paid partnership clearly in your caption or on-screen — #ad, #sponsored, or #KickHealthPartner all work.',
  'Speak from your own personal experience. Share what the product has done for you specifically.',
  'Mention that you got your product through the Kick Health creator partnership program.',
  'Tag @kickhealth in your post and/or caption.',
  'Film in clean, well-lit environments with clear audio. Natural light is preferred.',
  'Submit your content for review at least 5 business days before your intended publish date.',
];
const DONTS = [
  'Make specific medical claims — avoid language like "cures anxiety" or "guaranteed to work."',
  'Name or compare Kick Health products against competitor brands.',
  'Post content that has not been reviewed and approved by the Kick Health team first.',
  'Use stock footage, AI-generated faces, or AI-generated voiceovers in your content.',
  'Share your personal intake code publicly — it is for your use only.',
  'Discuss specific pricing or promotional offers unless explicitly provided in your brief.',
];

export function BrandGuidelinesView() {
  const { data } = useCreatorMe();
  const qc = useQueryClient();
  const [ack, setAck] = useState(false);

  const submit = useMutation({
    mutationFn: () => api.post('/api/creator/acknowledge-guidelines'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator', 'me'] }),
  });

  if (!data) return null;

  return (
    <>
      <div className={styles.pageActionRow} style={{ marginBottom: 16 }}>
        <div>
          <div className={styles.pageTitle}>Brand Guidelines</div>
          <div className={styles.pageSubtitle}>
            What to keep in mind when creating your Kick Health content.
          </div>
        </div>
      </div>

      <div className={styles.fillerBanner}>
        <span>⚠️</span>
        PLACEHOLDER — This is filler content. Replace with the official Kick Health brand
        guidelines PDF before launch.
      </div>

      <div className={styles.dosDontsGrid}>
        <div className={`${styles.card} ${styles.ddCol}`}>
          <div className={`${styles.ddHeader} ${styles.ddDo}`}>
            <span className={`${styles.ddDot} ${styles.ddDotDo}`} />
            <span className={styles.ddHeaderLabel}>Do</span>
          </div>
          <div className={styles.ddList}>
            {DOS.map((t) => (
              <div key={t} className={styles.ddItem}>
                <span className={`${styles.ddMarker} ${styles.ddMarkerDo}`}>✓</span>
                <div className={styles.ddText}>{t}</div>
              </div>
            ))}
          </div>
        </div>
        <div className={`${styles.card} ${styles.ddCol}`}>
          <div className={`${styles.ddHeader} ${styles.ddDont}`}>
            <span className={`${styles.ddDot} ${styles.ddDotDont}`} />
            <span className={styles.ddHeaderLabel}>Don't</span>
          </div>
          <div className={styles.ddList}>
            {DONTS.map((t) => (
              <div key={t} className={styles.ddItem}>
                <span className={`${styles.ddMarker} ${styles.ddMarkerDont}`}>✕</span>
                <div className={styles.ddText}>{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>Brand voice</div>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.voiceGrid}>
            <div className={styles.voicePill}>
              <div className={styles.voicePillTitle}>Authentic</div>
              <div className={styles.voicePillSub}>Real experiences, your own words</div>
            </div>
            <div className={styles.voicePill}>
              <div className={styles.voicePillTitle}>Conversational</div>
              <div className={styles.voicePillSub}>Talk to your audience, not at them</div>
            </div>
            <div className={styles.voicePill}>
              <div className={styles.voicePillTitle}>Empowering</div>
              <div className={styles.voicePillSub}>Focus on what's possible, not fear</div>
            </div>
          </div>
        </div>
      </div>

      {data.brand_guidelines_acknowledged ? (
        <div className={styles.glAckConfirmed}>
          <span className={styles.glAckCheck}>✓</span>
          <div>
            <div className={styles.glAckLabel}>Guidelines acknowledged</div>
            <div className={styles.glAckDate}>
              {formatDate(data.brand_guidelines_acknowledged_at)}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.glAckBox}>
          <label className={styles.glAckLabelRow}>
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            <span>I have read and understand the Kick Health brand guidelines</span>
          </label>
          <button
            type="button"
            className={styles.glAckBtn}
            disabled={!ack || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Saving…' : 'Confirm & Continue'}
          </button>
        </div>
      )}
    </>
  );
}
